import React from "react";
import {
  Link,
  MetaFunction,
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from "@remix-run/react";
import { Fragment, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Image,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Skeleton,
  useDisclosure,
  Tooltip,
} from "@heroui/react";
import { MdAccountCircle, MdStar } from "react-icons/md";
import { SendPriceModal } from "app/components/Product/SendPriceModal";
import "../../style.css";
import {
  fetchProductStockLevelApi,
  getProductRatingApi,
  markProductAsViewedApi,
} from "app/api/product/products.api";
import {
  camelCaseToTitleCase,
  formatToTwoDecimalPlaces,
  isAdditionType,
  hasValidBrandingPositions,
  toTitleCase,
} from "app/utils/fn";
import { useQuery } from "@tanstack/react-query";
import StockStatus, { ColorStockData } from "app/components/Product/StockDataV2";
import { useAtom } from "jotai";
import { ActionFunction, LoaderFunctionArgs } from "@remix-run/node";
import { toast, ToastContainer } from "react-toastify";
import { loginAction } from "app/actions/login.action";
import {
  BrandingColor,
  BrandingOption,
  DecorationOption,
  PriceOpt,
  ProductObject,
} from "app/api/product/product.type";
import { cartedProductT, CartItemT } from "app/api/cart/cart.type";
import { addToCartApi } from "app/api/cart/cart.api";
import ProductInfoDisplay from "app/components/Product/ProductInfoDisplay";
import SliderWithDynamicMarkers from "app/components/ProductSlider";
// import converter from "number-to-words";
import Breadcrumb from "app/components/Product/Breadcrumb";
const RequestForm = React.lazy(() => import("app/components/RequestQuote/RequestForm"));
const SimilarProduct = React.lazy(() => import("app/components/Product/SimilarProduct"));
import { useAdjustedProductQty, useDiscountSettings, computeProductPrice } from "app/hooks/product";
import { cartList } from "app/atoms/cart.atom";
import { UserType } from "app/api/user/user.types";
import ProductInfo from "app/components/ProductPage/ProductInfo";
import { userAtom } from "app/atoms/user.atom";
import ProductStockCheckDetails from "app/components/ProductPage/ProductStockCheckDetails";
import ExtraParamComponent from "app/components/Product/ExtraParamComponent";
import { BsCart2, BsCheckCircleFill } from "react-icons/bs";
import { MdQuestionMark } from "react-icons/md";
import { GiPriceTag } from "react-icons/gi";
import { TfiWrite } from "react-icons/tfi";
import appaImg from "app/assets/appa-sponsor.png";
import { ClientOnly } from "remix-utils/client-only";
import { productPageSchema } from "../_index_seo";
import { LoaderData, productLoader } from "app/loaders/product.loader";
import { productMeta } from "app/loaders/product.meta";
import { trackViewItem, trackAddToCart } from "app/utils/analytics";
// import { addViewedProductsAtom } from "app/atoms/utils.atom";

export const meta: MetaFunction<LoaderData> = (arg: any) => {
  return productMeta(arg);
};

export const loader = async (afg: LoaderFunctionArgs) => {
  return productLoader(afg);
};

export const action: ActionFunction = async ({ request }) => {
  loginAction(request);
};

export default React.memo(function ProductDetailsRoute() {
  const data = useLoaderData<LoaderData>();

  const [cartsList, setCartList] = useAtom(cartList);
  const [user] = useAtom(userAtom);
  // const [, addViewedProduct] = useAtom(addViewedProductsAtom);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { category, subcategory } = useParams();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const buyRef = useRef<HTMLDivElement>(null);
  const priceBoxRef = useRef<HTMLDivElement>(null);
  const quantitySectionRef = useRef<HTMLDivElement>(null);
  const productInfoDesktopRef = useRef<HTMLDivElement>(null);
  const productInfoMobileRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [productInfoTab, setProductInfoTab] = useState<string>("Description");

  // Reset tab to Description when URL changes (e.g. navigating to a different product)
  useEffect(() => {
    setProductInfoTab("Description");
  }, [pathname]);

  // Intersection Observer for Price Box
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show sticky bar when price box is NOT intersecting (scrolled out of view)
        // and we are scrolling down (boundingClientRect.top is negative)
        setShowStickyBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      {
        threshold: 0,
        rootMargin: "-100px 0px 0px 0px", // Offset to trigger slightly before/after
      },
    );

    if (priceBoxRef.current) {
      observer.observe(priceBoxRef.current);
    }

    return () => {
      if (priceBoxRef.current) {
        observer.unobserve(priceBoxRef.current);
      }
    };
  }, []);

  // Memoize stable values to prevent unnecessary re-renders
  const productId = useMemo(() => data?.product?._id, [data?.product?._id]);
  const productSlug = useMemo(() => data?.product?.slug, [data?.product?.slug]);
  const canCheckStock = useMemo(
    () => data?.product?.meta?.canCheckStock,
    [data?.product?.meta?.canCheckStock],
  );
  const isDiscontinued = useMemo(
    () => data?.product?.meta?.discontinued,
    [data?.product?.meta?.discontinued],
  );

  // Local state
  const [productQty, setProductQty] = useState(data?.product?.quantity?.min || 1);
  const [currentPrice, setCurrentPrice] = useState<{
    initialBasePrice: number;
    basePrice: number;
    totalAdditionsPrice: number;
    grandTotal: number;
    actualUnitPrice: number;
    fixedUnitPrice: number;
    fixedGrandTotal: number;
  }>({
    initialBasePrice: 0,
    basePrice: 0,
    totalAdditionsPrice: 0,
    grandTotal: 0,
    actualUnitPrice: 0,
    fixedUnitPrice: 0,
    fixedGrandTotal: 0,
  });
  const [currentBrandingType, setBrandingType] = useState<{ name: string; friendlyName: string }>({
    name: "",
    friendlyName: "",
  });
  const [selectedArtColor, setArtColor] = useState<keyof BrandingColor>("positions");
  const [selectedBrandingPosition, setBrandingPosition] = useState<{
    friendlyName: string;
    additionName: string;
  }>({
    friendlyName: "",
    additionName: "",
  });
  const [_selectedAddition, setSelectedAddition] = useState<string | null>(null);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [mainAdditions, setMainAdditions] = useState<PriceOpt | undefined>(undefined);
  const [availableStock, setAvailableStock] = useState<boolean>(false);
  const [availableColors, setAvailableColors] = useState<{ name?: string }[]>([]);
  const [selectedColor, setSelectedColor] = useState<ColorStockData>({
    id: "",
    name: "",
    code: "",
    status: "",
    size: "",
    qtyOnHand: 0,
  });
  const [minMarkup, setMinMarkUp] = useState<number>(0);
  const [profitDiscount, setProfitDiscount] = useState<number>(0);
  const [averageRate, setAverageRate] = useState<number>(0);
  const [reviewsCount, setReviewsCount] = useState<number>(0);
  const [quoteProduct, setQuoteProduct] = useState<{
    url: string;
    product: ProductObject;
    artColorList: string[];
    productQty: number;
    selectedBranding: {
      color?: ColorStockData;
      brandingType?: { name: string; friendlyName: string };
      artColor?: keyof BrandingColor;
      brandingPosition?: string;
      qty?: number;
    };
    branding: Array<{
      type: string;
      positions: Array<{
        value: string;
        label: string;
        extraParams: Array<{
          key: string;
          values: string[];
        }>;
      }>;
    }>;
  }>();
  const [decorationOptions, setDecorationOptions] = useState<{
    [key: string]: BrandingOption;
  }>({});
  const [isAddingToCartLoading, setIsAddingToCartLoading] = useState<boolean>(false);
  const [shouldShowRequestQuote, setShouldShowRequestQuote] = useState<boolean>(false);

  const {
    isOpen: requestIsOpen,
    onOpen: requestOnOpen,
    onClose: requestOnClose,
    onOpenChange: requestOnOpenChange,
  } = useDisclosure();

  const {
    data: stockData,
    isLoading: isStockDataLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["stockData", productId, productSlug],
    queryFn: () =>
      canCheckStock && !isDiscontinued && fetchProductStockLevelApi(data?.product?.meta?.id),
    staleTime: Infinity,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    enabled: Boolean(canCheckStock && !isDiscontinued && productId),
  });

  // Adjust product quantity according to minimum and available stock
  const [adjustedQty] = useAdjustedProductQty(
    productQty,
    data?.product?.quantity?.min,
    selectedColor,
  );

  useEffect(() => {
    setProductQty(adjustedQty);
  }, [adjustedQty]);

  // Fetch product rating from server (Redis-cached)
  useEffect(() => {
    if (!productId) return;
    getProductRatingApi(productId)
      .then(({ data }) => {
        setAverageRate(data?.averageRating ?? 0);
        setReviewsCount(data?.reviewsCount ?? 0);
      })
      .catch(() => {});
  }, [productId]);

  const scrollToQuantitySection = useCallback(() => {
    if (!quantitySectionRef.current || typeof window === "undefined") return;

    const stickyBarOffset = window.innerWidth >= 1024 ? 120 : 160;
    const quantitySectionTop =
      quantitySectionRef.current.getBoundingClientRect().top + window.scrollY - stickyBarOffset;

    window.scrollTo({
      top: Math.max(quantitySectionTop, 0),
      behavior: "smooth",
    });
  }, []);

  const handleStockCheck = useCallback((v: boolean, color?: ColorStockData) => {
    setAvailableStock(v);
    if (color) setSelectedColor(color);
  }, []);

  const priceBlockInventoryStatus = useMemo(() => {
    if (isStockDataLoading) {
      return null;
    }

    const minQty = data?.product?.quantity?.min || 1;

    if (!selectedColor?.id) {
      return availableStock
        ? {
            label: "In Stock",
            textClassName: "text-green-600 dark:text-green-400",
            dotClassName: "bg-green-500",
          }
        : {
            label: "Check Availability",
            textClassName: "text-amber-600 dark:text-amber-400",
            dotClassName: "bg-amber-500",
          };
    }

    if (!availableStock || selectedColor.qtyOnHand <= 0) {
      return {
        label: selectedColor.status || "Out of Stock",
        textClassName: "text-red-600 dark:text-red-400",
        dotClassName: "bg-red-500",
      };
    }

    if (selectedColor.qtyOnHand < minQty || productQty > selectedColor.qtyOnHand) {
      return {
        label: "Insufficient Stock",
        textClassName: "text-amber-600 dark:text-amber-400",
        dotClassName: "bg-amber-500",
      };
    }

    return {
      label: selectedColor.status || "In Stock",
      textClassName: "text-green-600 dark:text-green-400",
      dotClassName: "bg-green-500",
    };
  }, [availableStock, data?.product?.quantity?.min, isStockDataLoading, productQty, selectedColor]);

  const addToCart = useCallback(
    async (
      product: ProductObject,
      price: number,
      grandTotal?: number,
      selectedColorValue?: ColorStockData,
    ) => {
      setIsAddingToCartLoading(true);
      const cart: CartItemT[] = cartsList;
      const sessionId = crypto.randomUUID();

      const newCartItem: CartItemT = {
        product_Id: product?._id,
        supplierId: product.supplier._id,
        sessionId,
        productId: {
          _id: product?._id,
          overview: product.overview,
          price: product.price,
          quantity: product.quantity,
          slug: product.slug,
          globalProductCategory: product.globalProductCategory,
        } as cartedProductT,
        price: price,
        userId: data?.user?.uid ?? undefined,
        quantity: productQty || 1,
        isCheckedOut: false,
        attributes: [
          {
            key: "colour",
            value: `${selectedColorValue?.name || ""}${selectedColorValue?.size || ""}`?.trim(),
          },
        ],
        brandingInfo: {
          additionsIndex: 0,
          type: currentBrandingType?.friendlyName || currentBrandingType?.name,
          position: selectedBrandingPosition.friendlyName || selectedBrandingPosition.additionName,
          artwork: camelCaseToTitleCase(selectedArtColor),
          additionsPrice: mainAdditions?.priceBreaks?.[0]?.price || 0,
          description: mainAdditions?.description || "",
        },
      };

      try {
        const response = await addToCartApi(newCartItem);

        if (response?.data?._id) {
          const updatedCart = [...cart, { ...newCartItem, _id: response.data._id }];
          setCartList(updatedCart);
          trackAddToCart(product, productQty || 1, price, grandTotal ?? price * (productQty || 1));
          navigate("/cart");
        } else {
          toast.error("Unexpected response from server. Product not added to cart.", {
            containerId: "loginToast",
          });
        }
      } catch (error: any) {
        const isNetworkError = error?.message === "Network Error" || !error?.response;

        if (isNetworkError) {
          toast.error("Network error occurred. Please check your connection.", {
            containerId: "loginToast",
          });
        } else {
          const errorMessage = Array.isArray(error?.response?.data?.message)
            ? error.response.data.message.join(", ")
            : error?.response?.data?.message || "Error adding product to cart";

          toast.error(errorMessage || error?.message, {
            containerId: "loginToast",
          });
        }
      } finally {
        setIsAddingToCartLoading(false);
      }
    },
    [
      cartsList,
      data?.user?.uid,
      productQty,
      currentBrandingType?.name,
      selectedBrandingPosition,
      selectedArtColor,
      mainAdditions,
      setCartList,
      navigate,
    ],
  );

  const handleBuyNow = useCallback(() => {
    if (currentPrice) {
      addToCart(data.product, currentPrice.actualUnitPrice, currentPrice.grandTotal, selectedColor);
    }
  }, [data.product, currentPrice, selectedColor, addToCart]);

  const handleAdditionalDecorationSelection = useCallback(
    (additionKey: string) => {
      const matchingAdditions = data?.product?.product?.prices?.priceGroups.flatMap(
        (v: any) => v.additions,
      );

      const matchingAddition = matchingAdditions.find((v: any) => v.description === additionKey);

      setMainAdditions(matchingAddition || undefined);
    },
    [data?.product?.product?.prices?.priceGroups],
  );

  // Normalize words function - shared utility
  const normalizeWords = useCallback((str: string) => {
    return str
      .trim()
      .replace(/\s+/g, " ")
      .split(" ")
      .map((word) => word.trim().toLowerCase())
      .filter((word) => word.length > 0);
    // .sort();
  }, []);

  // Memoize normalized addition descriptions for performance
  const normalizedAdditions = useMemo(() => {
    if (!data?.product?.product?.prices?.priceGroups) return [];

    return data.product.product.prices.priceGroups
      .flatMap((v: any) => v.additions)
      .map((addition: any) => ({
        original: addition,
        normalized: normalizeWords(addition.description),
      }));
  }, [data?.product?.product?.prices?.priceGroups, normalizeWords]);

  // Check if position value exists in price groups
  const isPositionInPriceGroups = useCallback(
    (positionValue: string, brandingType: string) => {
      const result = normalizedAdditions.some(
        ({ _normalized, original }: { _normalized: string[]; original: any }) => {
          return original.type === brandingType && positionValue === original.description;
        },
      );
      return result;
    },
    [normalizedAdditions, normalizeWords],
  );

  const handleQtyChange = useCallback((val: number) => {
    setProductQty(val);
    setShowStickyBar(true); // show sticky footer so user sees updated price
  }, []);

  const scrollToBuy = useCallback(() => {
    buyRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const scrollToReviews = useCallback(() => {
    setProductInfoTab("Reviews");
    const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
    const el = isDesktop ? productInfoDesktopRef.current : productInfoMobileRef.current;
    el?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useDiscountSettings(
    data,
    currentPrice,
    currentBrandingType,
    decorationOptions,
    selectedArtColor,
    selectedBrandingPosition.additionName,
    handleAdditionalDecorationSelection,
    setMinMarkUp,
    setProfitDiscount,
  );

  useEffect(() => {
    if (user?._id && productId && user?.accessToken) {
      markProductAsViewedApi(productId, {
        Authorization: `Bearer ${user.accessToken}`,
        "x-uid": user._id,
      });
    }
  }, [user?._id, user?.accessToken, productId]);

  const fromPrice = useMemo(() => {
    const resolvedUserType = user?.userType || data?.user?.userType || UserType.Non_VIP;
    const priceRange =
      data?.product?.userTypePrice?.[resolvedUserType] ||
      data?.product?.userTypePrice?.[UserType.Non_VIP] ||
      data?.product?.price;

    if (priceRange?.min > 0) {
      return priceRange.min;
    }

    const basePriceBreaks =
      data?.product?.product?.prices?.priceGroups?.[0]?.basePrice?.priceBreaks || [];
    const availableBreakPrices = basePriceBreaks
      .map((priceBreak: { price?: number }) => Number(priceBreak?.price) || 0)
      .filter((price: number) => price > 0);

    if (availableBreakPrices.length > 0) {
      return Math.min(...availableBreakPrices);
    }

    return currentPrice?.actualUnitPrice || 0;
  }, [
    currentPrice?.actualUnitPrice,
    data?.product?.price,
    data?.product?.product?.prices?.priceGroups,
    data?.product?.userTypePrice,
    data?.user?.userType,
    user?.userType,
  ]);

  // Fire view_item once the price is first calculated
  const hasTrackedViewItem = useRef(false);
  useEffect(() => {
    if (!hasTrackedViewItem.current && currentPrice?.actualUnitPrice > 0 && data?.product) {
      trackViewItem(data.product, currentPrice.actualUnitPrice);
      hasTrackedViewItem.current = true;
    }
  }, [currentPrice?.actualUnitPrice, data?.product]);

  // Recalculate price when dependencies change
  useEffect(() => {
    const basePrice = data?.product?.product?.prices?.priceGroups?.[0]?.basePrice;
    console.log("basePrice", basePrice);
    if (basePrice) {
      const newPrice = computeProductPrice(
        basePrice,
        productQty,
        mainAdditions,
        minMarkup,
        profitDiscount,
        data.product.supplier.isSpecialSupplier,
        data.product.quantity.min,
      );
      setCurrentPrice((prev: any) => {
        if (
          !prev ||
          Number(prev.basePrice) !== Number(newPrice?.basePrice) ||
          Number(prev.totalAdditionsPrice) !== Number(newPrice.totalAdditionsPrice) ||
          Number(prev.grandTotal) !== Number(newPrice.grandTotal)
        ) {
          return newPrice;
        }
        return prev;
      });
    }
  }, [
    productQty,
    mainAdditions,
    minMarkup,
    profitDiscount,
    data?.product?.product?.prices?.priceGroups,
    data?.product?.supplier?.isSpecialSupplier,
    data?.product?.quantity?.min,
  ]);

  // Memoize decoration options to prevent recreation
  const memoizedDecorationOptions = useMemo(() => {
    const supplierOptions = data?.product?.supplier?.decorationOptions;
    if (!supplierOptions) return {};

    return supplierOptions.reduce(
      (acc: { [key: string]: BrandingOption }, option: DecorationOption) => {
        const { type, positions } = option;
        const result: Partial<BrandingOption> = {
          type: type?.name,
          friendlyName: type?.friendlyName || "",
          description: type?.description || "",
        };
        if (positions?.length) result.positions = positions;
        if (result.positions) {
          acc[type?.name] = result as BrandingOption;
        }
        return acc;
      },
      {} as { [key: string]: BrandingOption },
    );
  }, [data?.product?.supplier?.decorationOptions]);

  // Set decoration options from supplier data if present
  useEffect(() => {
    setDecorationOptions(memoizedDecorationOptions);
    setIsSelected(false);
  }, [memoizedDecorationOptions]);

  // Stable reference for decoration options to prevent child re-renders
  const stableDecorationOptions = useMemo(() => decorationOptions, [decorationOptions]);

  // Map decoration types to their positions, filtering by valid types and positions
  const decorationTypeToPositionsMap = useMemo(() => {
    const result: Record<string, Array<{ friendlyName: string; value: string }>> = {};

    Object.keys(stableDecorationOptions).forEach((optionKey) => {
      const option = stableDecorationOptions[optionKey];

      // Check if this decoration type exists on the product and has valid branding positions
      if (
        isAdditionType(data.product?.product?.prices?.priceGroups, optionKey) &&
        hasValidBrandingPositions(
          stableDecorationOptions[optionKey],
          option.type, //data.product?.product?.prices?.priceGroups,
          isPositionInPriceGroups,
        )
      ) {
        const positions: Array<{ friendlyName: string; value: string }> = [];

        // Handle positions format
        if (option.positions && Array.isArray(option.positions)) {
          option.positions.forEach((position: any) => {
            if (position.value && isPositionInPriceGroups(position.value, optionKey)) {
              positions.push({
                friendlyName: position.label || toTitleCase(position.value),
                value: position.value,
              });
            }
          });
        }

        // Deduplicate positions by value
        const uniquePositions = positions.filter(
          (position, index, self) => index === self.findIndex((p) => p.value === position.value),
        );

        if (uniquePositions.length > 0) {
          result[option?.friendlyName || optionKey] = uniquePositions;
        }
      }
    });

    return result;
  }, [
    stableDecorationOptions,
    data.product?.product?.prices?.priceGroups,
    isPositionInPriceGroups,
  ]);

  // Effective purchase type from Advanced Buy Now hierarchy: supplier > category > subcategory > product
  const purchaseTypeMemo = useMemo(() => {
    const supplierStatus = data?.product?.supplier?.status;
    const categoryStatus = data?.product?.category?.status;
    const subcategoryStatus = data?.product?.subCategory?.status;
    const productStatus = data?.product?.status;

    let effectiveStatus = productStatus ?? "buy now";
    if (supplierStatus === "request quote") effectiveStatus = "request quote";
    else if (categoryStatus === "request quote") effectiveStatus = "request quote";
    else if (subcategoryStatus === "request quote") effectiveStatus = "request quote";

    return {
      effectiveStatus,
      isStatusRequestQuote: effectiveStatus === "request quote",
    };
  }, [
    data?.product?.supplier?.status,
    data?.product?.category?.status,
    data?.product?.subCategory?.status,
    data?.product?.status,
  ]);

  // Memoize the shouldShowRequestQuote calculation
  const shouldShowRequestQuoteMemo = useMemo(() => {
    const { product, purchaseSettings } = data ?? {};

    const minPrice = product?.userTypePrice?.["Non-VIP"]?.min;
    const minQuantity = product?.quantity?.min;
    const isBuyNowDisabled = purchaseSettings?.defaultBuyNowCandidateStatus === "disabled";
    const isQuoteRequired = currentPrice?.grandTotal >= purchaseSettings?.defaultQuotationRequest;

    const canCheckStock = data?.product?.meta?.canCheckStock;
    const isStatusRequestQuote = purchaseTypeMemo.isStatusRequestQuote;

    return (
      !isStockDataLoading &&
      currentPrice &&
      (isSelected ||
        !minPrice ||
        !availableStock ||
        isBuyNowDisabled ||
        isQuoteRequired ||
        !canCheckStock ||
        productQty < minQuantity ||
        productQty > selectedColor.qtyOnHand ||
        isStatusRequestQuote)
    );
  }, [
    isStockDataLoading,
    currentPrice,
    availableStock,
    isSelected,
    data?.product?.price?.min,
    data?.product?.quantity?.min,
    data?.purchaseSettings?.defaultBuyNowCandidateStatus,
    data?.purchaseSettings?.defaultQuotationRequest,
    data?.product?.meta?.canCheckStock,
    purchaseTypeMemo.isStatusRequestQuote,
    productQty,
    selectedColor.qtyOnHand,
  ]);

  useEffect(() => {
    setShouldShowRequestQuote(Boolean(shouldShowRequestQuoteMemo));
  }, [shouldShowRequestQuoteMemo]);

  const handleRequestQuote = useCallback(() => {
    const product = data?.product;
    if (product?._id) {
      requestOnOpen();
      const _product = JSON.parse(JSON.stringify(product));
      const updatedColors = [
        {
          name: `${selectedColor?.name}${
            selectedColor.size ? ` ${selectedColor.size}` : ""
          }`?.trim(),
          id: selectedColor.id,
        },
        ...availableColors,
      ];
      const firstColor = updatedColors[0];
      const uniqueRestColors = Array.from(
        new Map(updatedColors.slice(1).map((color) => [color?.name, color])).values(),
      );
      const uniqueColors = [
        ...(firstColor && firstColor?.name !== "" ? [firstColor] : []),
        ...uniqueRestColors.filter((c) => c?.name !== firstColor?.name),
      ];

      if (uniqueColors && uniqueColors.length > 0) {
        _product.product.colours.list = uniqueColors;
      }
      setQuoteProduct({
        url: typeof document !== "undefined" ? document.location.href : "",
        product: _product,
        artColorList: currentBrandingType?.name
          ? Object.keys(stableDecorationOptions[currentBrandingType?.name]).filter(
              (v) => v !== "friendlyName" && v !== "type",
            )
          : [],
        productQty,
        selectedBranding: {
          color: selectedColor,
          brandingType: currentBrandingType,
          artColor: selectedArtColor,
          brandingPosition: (() => {
            // Find the friendly name for the selected branding position
            if (currentBrandingType?.name && stableDecorationOptions[currentBrandingType.name]) {
              const decorationOption = stableDecorationOptions[currentBrandingType.name] as any;
              if (decorationOption.positions && Array.isArray(decorationOption.positions)) {
                const position = decorationOption.positions.find(
                  (pos: any) => pos.value === selectedBrandingPosition.additionName,
                );
                return position?.label || position?.value || selectedBrandingPosition.friendlyName;
              }
            }
            return selectedBrandingPosition.friendlyName || selectedBrandingPosition.additionName;
          })(),
          qty: productQty,
        },
        branding: Object.keys(stableDecorationOptions)
          .filter(
            (brandingType) =>
              isAdditionType(data?.product?.product?.prices?.priceGroups, brandingType) &&
              hasValidBrandingPositions(
                stableDecorationOptions[brandingType],
                brandingType, //data?.product?.product?.prices?.priceGroups,
                isPositionInPriceGroups,
              ),
          )
          .map((brandingType) => {
            const decorationOption = stableDecorationOptions[brandingType];
            const positions = decorationOption.positions || [];

            return {
              type: decorationOption.type || brandingType,
              friendlyName: decorationOption.friendlyName || brandingType,
              positions: positions
                .filter(
                  (position: any) =>
                    position.value && isPositionInPriceGroups(position.value, brandingType),
                )
                .map((position: any) => ({
                  value: position.value || position.name || "",
                  label: position.label || position.name || "",
                  extraParams: position.extraParams || [],
                })),
            };
          }),
      });
    }
  }, [
    data?.product,
    selectedColor,
    availableColors,
    currentBrandingType,
    stableDecorationOptions,
    selectedArtColor,
    selectedBrandingPosition,
    productQty,
    requestOnOpen,
  ]);

  const isBrandingAvailable = useMemo(
    () => data?.product?.product?.prices?.priceGroups?.[0]?.additions.length > 0,
    [data?.product?.product?.prices?.priceGroups],
  );

  useEffect(() => {
    if (productSlug) {
      setMainAdditions(undefined);
      setBrandingType({ name: "", friendlyName: "" });
    }
  }, [productSlug]);

  // Calculate original price (price at minimum quantity with same VIP settings)
  const getOriginalPrice = useCallback(() => {
    if (!data?.product?.product?.prices?.priceGroups?.[0]?.basePrice) {
      return null;
    }

    const basePrice = data.product.product.prices.priceGroups[0].basePrice;
    const minQuantity = data?.product?.quantity?.min || 1;

    // Calculate price at minimum quantity using the same VIP settings as current price
    const originalPrice = computeProductPrice(
      basePrice,
      minQuantity,
      mainAdditions,
      minMarkup,
      profitDiscount,
      data?.product?.supplier?.isSpecialSupplier || false,
      minQuantity,
    );

    return originalPrice;
  }, [
    data?.product?.product?.prices?.priceGroups,
    data?.product?.supplier?.isSpecialSupplier,
    data?.product?.quantity?.min,
    mainAdditions,
    minMarkup,
    profitDiscount,
  ]);

  // Calculate discount percentage based on price breaks
  const calculatePriceBreakDiscount = useCallback(() => {
    const originalPrice = getOriginalPrice();

    // Check if we have the necessary data
    if (!originalPrice || !currentPrice?.actualUnitPrice || currentPrice.actualUnitPrice <= 0) {
      return "0% OFF";
    }

    const originalUnitPrice = originalPrice.actualUnitPrice;
    const currentUnitPrice = currentPrice.actualUnitPrice;

    // If current price is higher than or equal to original, no discount
    if (currentUnitPrice >= originalUnitPrice) {
      return "0% OFF";
    }

    // Calculate discount percentage using formula:
    // (originalPrice - currentPrice) / originalPrice × 100%
    const difference = originalUnitPrice - currentUnitPrice;

    // Safety check to avoid division by zero
    if (originalUnitPrice <= 0) {
      return "0% OFF";
    }

    const discountPercentage = (difference / originalUnitPrice) * 100;

    if (discountPercentage <= 0) {
      return "0% OFF";
    }

    const result = `${Math.round(discountPercentage * 10) / 10}% OFF`;

    return result;
  }, [currentPrice, getOriginalPrice]);

  return (
    <Fragment key={`${productId}-${productSlug}`}>
      <ToastContainer containerId="loginToast" />

      {/* ─── Breadcrumb (visible on all screens; compact on mobile) ─── */}
      <div className="mb-3 px-2 sm:px-3 md:px-4 lg:px-0 lg:mb-6">
        <Breadcrumb
          category={category || ""}
          subCategory={subcategory || ""}
          productOverview={data?.product?.overview}
        />
      </div>

      <div className="w-full mx-auto px-0 lg:px-0">
        <div className="flex flex-col lg:flex-row gap-0 lg:gap-8 items-start">
          {/* LEFT ── Sticky image gallery (45%) */}
          <div className="w-full lg:w-[50%] lg:sticky lg:top-4 px-0">
            <ProductInfoDisplay
              imageOnly
              images={data?.product?.product?.images}
              title={data?.product?.overview?.name}
              productCode={data?.product?.overview?.code}
              description={data?.product?.product?.description}
            />
            <div
              ref={productInfoDesktopRef}
              className="mt-4 md:mt-10 pb-20 md:pb-0 hidden lg:block"
            >
              <ProductInfo
                product={data?.product}
                selectedKey={productInfoTab}
                onSelectionChange={(key) => setProductInfoTab(String(key))}
              />
            </div>
            {/* APPA Trust Badge */}
            <Link to="/guarantees" className="hidden lg:block">
              <Image
                alt="appa-sponsor"
                className="mt-4"
                radius="md"
                src={appaImg}
                removeWrapper
                fallbackSrc
              />
            </Link>
          </div>

          {/* RIGHT ── Product details + purchase panel (55%) */}
          <div className="w-full lg:w-[50%] space-y-0 md:space-y-4">
            {/* Title */}
            <div className="flex flex-col gap-2 w-full px-2 sm:px-3 pt-4 md:px-0 md:pt-0">
              <div className="flex flex-col gap-2 mb-2 lg:mb-1">
                <div className="flex items-center gap-2">
                  <Tooltip
                    content={
                      <div className="px-1 py-2 max-w-sm">
                        <div className="text-sm lg:text-base font-bold mb-1">
                          Product Description
                        </div>
                        <div className="text-xs lg:text-sm line-clamp-6">
                          {data?.product?.product?.description}
                        </div>
                      </div>
                    }
                    delay={0}
                    closeDelay={0}
                    offset={10}
                    className="max-w-[85%]"
                  >
                    <h1 className="text-lg md:text-xl lg:text-3xl font-bold text-foreground leading-snug cursor-help">
                      {data?.product?.overview?.name}
                    </h1>
                  </Tooltip>
                  <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-full h-fit whitespace-nowrap">
                    {data?.product?.overview?.code}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-primary font-semibold sm:font-bold text-sm sm:text-sm md:text-base">
                    From{" "}
                    <span className="">
                      ${formatToTwoDecimalPlaces(fromPrice)}{" "}
                      <span className=" text-xs">ex GST</span>
                    </span>
                  </div>
                  {!data?.user?.uid && (
                    <Link
                      to="/auth/login"
                      className="inline-flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-700 hover:underline transition-colors group shrink-0"
                    >
                      <MdAccountCircle size={24} className="text-lg text-primary" />
                      <div className="flex flex-col items-center">
                        <div className=" font-semibold text-md">Have an account?</div>
                        <div className="font-medium"> Please Login first</div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Rating & average rating */}
              {averageRate > 0 && (
                <button
                  type="button"
                  onClick={scrollToReviews}
                  className="flex items-center gap-2 mb-2 md:mb-1 cursor-pointer hover:opacity-80 transition-opacity text-left"
                >
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-foreground">{averageRate}</span>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <MdStar
                          key={i}
                          size="16"
                          className={
                            i < Math.round(averageRate) ? "text-amber-500" : "text-gray-300"
                          }
                        />
                      ))}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({reviewsCount || data?.product?.meta?.reviewsCount || 0} reviews)
                  </span>
                </button>
              )}
            </div>

            <Divider className=" hidden md:block my-3 md:my-4" />

            {/* ── Price Box ── */}
            <div
              ref={priceBoxRef}
              className="bg-blue-50 dark:bg-blue-900 border border-blue-50 dark:border-blue-800 mx-2  lg:mx-0  rounded-none md:rounded-xl p-4 md:p-6 space-y-3"
            >
              {/* Mobile: list price + save % then sale price (green); Desktop: unit + total */}
              <div className="flex flex-col gap-3 md:gap-4">
                {/* Unit price and Total on same row, Total far right */}
                <div className="flex flex-row items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="text-2xl md:text-4xl font-bold  text-primary md:dark:text-white">
                      {currentPrice?.actualUnitPrice > 0
                        ? `$${formatToTwoDecimalPlaces(currentPrice.actualUnitPrice)}`
                        : "—"}
                    </span>
                    <span className="text-xs md:text-sm font-medium text-gray-500 dark:text-gray-100 self-center">
                      <span className="md:hidden">Sale Price</span>
                      <span className="hidden md:inline">Each (ex. GST)</span>
                    </span>
                  </div>
                  {currentPrice?.grandTotal > 0 && (
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-100">
                        Total (ex. GST)
                      </span>
                      <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                        ${formatToTwoDecimalPlaces(currentPrice.grandTotal)}
                      </span>
                    </div>
                  )}
                </div>
                {/* Desktop-only: WAS price + SAVE badge */}
                <div className="flex items-center gap-2 flex-wrap">
                  {(() => {
                    const origPrice = getOriginalPrice();
                    return (
                      origPrice &&
                      currentPrice &&
                      productQty > (data?.product?.quantity?.min || 1) &&
                      origPrice.actualUnitPrice > currentPrice.actualUnitPrice && (
                        <span className="text-sm font-medium text-gray-400 line-through">
                          WAS ${formatToTwoDecimalPlaces(origPrice.actualUnitPrice)}
                        </span>
                      )
                    );
                  })()}
                  {calculatePriceBreakDiscount() !== "0% OFF" && (
                    <span className="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 text-xs font-bold px-2.5 py-1 rounded-md">
                      SAVE {calculatePriceBreakDiscount()}
                    </span>
                  )}
                </div>
              </div>

              {/* Stock status */}
              <div className="flex items-center justify-center gap-3 py-2 before:content-[''] before:block before:flex-1 before:h-px before:border-t before:border-blue-200 dark:before:border-blue-700 after:content-[''] after:block after:flex-1 after:h-px after:border-t after:border-blue-200 dark:after:border-blue-700">
                {isStockDataLoading ? (
                  <Skeleton className="h-5 w-24 shrink-0 rounded-full" />
                ) : (
                  <span
                    className={`text-sm font-semibold flex shrink-0 items-center gap-1.5 ${
                      priceBlockInventoryStatus?.textClassName ||
                      "text-amber-600 dark:text-amber-400"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full inline-block ${
                        priceBlockInventoryStatus?.dotClassName || "bg-amber-500"
                      }`}
                    />
                    {priceBlockInventoryStatus?.label || "Check Availability"}
                  </span>
                )}
              </div>

              {/* Action buttons */}
              {isStockDataLoading ? (
                <div className="flex gap-3">
                  <Skeleton className="h-11 w-full rounded-lg" />
                  <Skeleton className="h-11 w-full rounded-lg" />
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {!shouldShowRequestQuote ? (
                    <Button
                      variant="solid"
                      color="primary"
                      radius="sm"
                      fullWidth
                      aria-label={user?.isCreditAccount ? "Place Order" : "Buy It Now"}
                      className="font-bold shadow-md h-11"
                      startContent={<BsCart2 className="text-lg" />}
                      isLoading={isAddingToCartLoading}
                      isDisabled={
                        productQty <= 0 ||
                        productQty < (data?.product?.quantity?.min || 1) ||
                        !availableStock ||
                        data?.purchaseSettings?.defaultBuyNowCandidateStatus === "disabled"
                      }
                      onPress={handleBuyNow}
                    >
                      <span className="inline">
                        {user?.isCreditAccount ? "Place Order" : "Buy It Now"}
                      </span>
                    </Button>
                  ) : (
                    <Button
                      variant="solid"
                      color="warning"
                      radius="sm"
                      fullWidth
                      aria-label="Request Quote"
                      className="font-bold shadow-md h-11 text-white"
                      startContent={<TfiWrite />}
                      onPress={handleRequestQuote}
                    >
                      Request Quote
                    </Button>
                  )}
                  <Button
                    variant="bordered"
                    color="primary"
                    radius="sm"
                    fullWidth
                    className="h-11 flex dark:text-white"
                    isDisabled={!availableStock}
                    startContent={<GiPriceTag className="text-lg dark:text-white" />}
                    onPress={onOpen}
                  >
                    Send Pricing
                  </Button>
                </div>
              )}

              {/* No Setup Fees / benefits (compact on mobile) */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400 pb-0 sm:pb-1">
                <div className="flex items-center gap-1.5">
                  <BsCheckCircleFill className="text-primary text-base shrink-0" />
                  <span>No Setup Fees</span>
                </div>
                <span className="hidden sm:inline text-zinc-300 dark:text-zinc-600">|</span>
                <span className="text-green-600 dark:text-green-400 font-medium">
                  Fast Shipping Australia wide
                </span>
              </div>
            </div>

            {/* ── Product Configuration (Stock & Quantity) ── */}
            <div className="bg-white dark:bg-zinc-800 !mt-2 md:!mt-4  border-b md:border-y md:border border-zinc-200 dark:border-zinc-700 rounded-none md:rounded-xl overflow-hidden md:shadow-sm">
              <div className="p-2 sm:px-3 md:px-4 border-b border-zinc-100 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/50 flex w-full justify-between items-center">
                <span className="w-fit text-lg font-bold text-gray-900 dark:text-white">
                  Product Details
                </span>
                <ProductStockCheckDetails
                  hideTitle
                  canCheckStock={data?.product?.meta?.canCheckStock}
                  discontinued={data?.product?.meta?.discontinued}
                  isStockDataLoading={isStockDataLoading}
                  refetch={refetch}
                  error={error}
                  productQty={productQty}
                  stockQuatity={selectedColor?.qtyOnHand || 0}
                  minQty={data?.product?.quantity?.min || 0}
                  availableStock={availableStock}
                />
              </div>

              <div className="p-2 sm:p-3 md:p-4 pt-2 space-y-6">
                {/* Stock Alerts */}
                {data?.product?.meta?.canCheckStock &&
                  !data?.product?.meta?.discontinued &&
                  !isStockDataLoading &&
                  !error &&
                  availableStock && (
                    <div className="space-y-2">
                      {selectedColor?.qtyOnHand < data?.product?.quantity?.min ? (
                        <Alert
                          color="warning"
                          title="Insufficient stock for minimum order. Please choose another option or request a quote."
                          classNames={{
                            base: "bg-amber-50 border border-amber-200 text-amber-800",
                          }}
                        />
                      ) : productQty > selectedColor?.qtyOnHand ? (
                        <Alert
                          color="warning"
                          title="Insufficient Stock for Selected Qty"
                          classNames={{
                            base: "bg-amber-50 border border-amber-200 text-amber-800",
                          }}
                        />
                      ) : productQty < data?.product?.quantity?.min ? (
                        <Alert
                          color="warning"
                          title={`Minimum order quantity is ${data?.product?.quantity?.min}. Please increase quantity.`}
                          classNames={{
                            base: "bg-amber-50 border border-amber-200 text-amber-800",
                          }}
                        />
                      ) : null}
                    </div>
                  )}

                {/* Colours & Quantity */}
                <div className="space-y-5 mt-2.5!">
                  {stockData && stockData.data && (
                    <StockStatus
                      slug={data?.product?.slug || ""}
                      getColors={(v) => v && setAvailableColors(v)}
                      stockData={stockData.data}
                      listData={data?.product?.product?.colours}
                      handleStockCheck={handleStockCheck}
                    />
                  )}

                  <div
                    ref={quantitySectionRef}
                    className="w-full bg-zinc-50 dark:bg-zinc-700/30 rounded-lg p-2 border border-zinc-100 dark:border-zinc-700"
                  >
                    <ClientOnly fallback={<Skeleton className="h-10 rounded-lg" />}>
                      {() => (
                        <div className="pt-2 px-0.5">
                          <SliderWithDynamicMarkers
                            productQty={productQty}
                            data={data.product}
                            handleQtyChange={(v) => handleQtyChange(Number(v))}
                          />
                        </div>
                      )}
                    </ClientOnly>
                  </div>
                </div>

                {/* Important Information */}
                {mainAdditions?.supplierNotes?.deliveryStock && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <MdQuestionMark className="text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="block text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Important Information
                        </span>
                        <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                          {mainAdditions?.supplierNotes?.deliveryStock ||
                            data?.product?.product?.prices?.priceGroups?.[0]?.basePrice
                              ?.supplierNotes?.deliveryStock}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Branding Section ── */}
            {isBrandingAvailable &&
              !isStockDataLoading &&
              data?.purchaseSettings?.defaultBuyNowCandidateStatus !== "disabled" && (
                <div
                  className="bg-white dark:bg-zinc-800 border-y md:border border-zinc-200 dark:border-zinc-700 rounded-none md:rounded-xl p-2 sm:p-3 md:p-4 space-y-5 md:shadow-sm"
                  ref={buyRef}
                >
                  {Object.keys(stableDecorationOptions).length > 0 &&
                    Object.keys(stableDecorationOptions).some(
                      (option) =>
                        isAdditionType(data.product?.product?.prices?.priceGroups, option) &&
                        hasValidBrandingPositions(
                          stableDecorationOptions[option],
                          option,
                          isPositionInPriceGroups,
                        ),
                    ) && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            Customisation Options
                          </h2>
                        </div>

                        {/* Branding Type Selection */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              3. Select a Branding
                            </span>
                            <Popover placement="top">
                              <PopoverTrigger>
                                <Button
                                  isIconOnly
                                  variant="light"
                                  size="sm"
                                  className="min-w-6 w-6 h-6 text-gray-400 hover:text-primary"
                                  aria-label="Branding options info"
                                >
                                  <MdQuestionMark size={14} />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="max-w-xs">
                                <div className="px-3 py-2">
                                  <div className="text-small font-bold mb-1">Branding Options</div>
                                  <div className="text-tiny text-gray-600">
                                    Choose how you want your logo applied to the product.
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>

                          <div className="flex flex-wrap gap-3">
                            {Object.keys(decorationTypeToPositionsMap)?.map((friendlyName) => {
                              const optionKey = Object.keys(stableDecorationOptions).find(
                                (key) =>
                                  stableDecorationOptions[key]?.friendlyName === friendlyName ||
                                  stableDecorationOptions[key]?.type === friendlyName,
                              );
                              if (!optionKey) return null;

                              const option = stableDecorationOptions[optionKey];
                              const isSelected = currentBrandingType?.name === option.type;

                              return (
                                <div
                                  key={optionKey}
                                  className={`
                                    relative flex gap-1 items-center justify-between group cursor-pointer rounded-lg px-2 sm:px-3 md:px-4 py-3 border transition-all duration-200
                                    ${
                                      isSelected
                                        ? "bg-primary/5 border-primary ring-1 ring-primary text-primary"
                                        : "bg-white dark:bg-zinc-700 border-zinc-200 dark:border-zinc-600 hover:border-primary/50 hover:shadow-sm"
                                    }
                                  `}
                                  onClick={() => {
                                    if (isSelected) {
                                      setBrandingType({ name: "", friendlyName: "" });
                                      setSelectedAddition(null);
                                      setBrandingPosition({ friendlyName: "", additionName: "" });
                                      setMainAdditions(undefined);
                                      return;
                                    }
                                    setBrandingType({
                                      name: option.type,
                                      friendlyName: option.friendlyName || option.type || "",
                                    });
                                    const positions = decorationTypeToPositionsMap[friendlyName];
                                    if (positions && positions.length > 0) {
                                      setArtColor("positions");
                                      const firstPosition = positions[0];
                                      setSelectedAddition(firstPosition.value);
                                      setBrandingPosition({
                                        friendlyName: firstPosition.friendlyName,
                                        additionName: firstPosition.value,
                                      });
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <div
                                      className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                                        isSelected
                                          ? "border-primary bg-primary"
                                          : "border-gray-300 bg-white"
                                      }`}
                                    >
                                      {isSelected && (
                                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                      )}
                                    </div>
                                    <span
                                      className={`font-medium text-sm ${
                                        isSelected
                                          ? "text-primary"
                                          : "text-gray-700 dark:text-gray-200"
                                      }`}
                                    >
                                      {option.friendlyName || toTitleCase(option.type)}
                                    </span>
                                  </div>

                                  {option?.description && (
                                    <div className=" hover:text-white hover:bg-primary/80  rounded-full transition-colors">
                                      <Popover placement="top">
                                        <PopoverTrigger>
                                          <Button
                                            isIconOnly
                                            variant="light"
                                            size="sm"
                                            className="min-w-6 w-6 h-6 text-gray-400 hover:text-white hover:bg-primary/80"
                                            aria-label="Branding option description"
                                          >
                                            <MdQuestionMark size={14} />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent>
                                          <div className="px-3 py-2 max-w-xs">
                                            <div className="text-tiny">{option?.description}</div>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Branding Positions Selection */}
                        {currentBrandingType?.friendlyName &&
                          decorationTypeToPositionsMap[currentBrandingType.friendlyName] && (
                            <div className="space-y-3 pt-2 border-t border-dashed border-zinc-200 dark:border-zinc-700">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                  4. Select Branding Position/s
                                </span>
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {decorationTypeToPositionsMap[currentBrandingType.friendlyName].map(
                                  (position) => (
                                    <div
                                      key={position.value}
                                      className={`
                                    cursor-pointer px-2 sm:px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-all
                                    ${
                                      selectedBrandingPosition.additionName === position.value
                                        ? "bg-primary text-white shadow-md shadow-primary/20"
                                        : "bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-zinc-600"
                                    }
                                  `}
                                      onClick={() => {
                                        setBrandingPosition({
                                          friendlyName: position.friendlyName,
                                          additionName: position.value,
                                        });
                                        setSelectedAddition(position.value);
                                      }}
                                    >
                                      {position.friendlyName}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                        <div className="pt-2">
                          <ExtraParamComponent
                            currentBrandingPosition={selectedBrandingPosition.additionName}
                            selectedArtColor={selectedArtColor}
                            currentBrandingType={currentBrandingType}
                            decorationOptions={stableDecorationOptions}
                          />
                        </div>
                      </div>
                    )}

                  {/* Pricing Summary / CTA Section */}
                  <div className="pt-2">
                    {!isSelected &&
                      availableStock &&
                      !shouldShowRequestQuote &&
                      data.product.userTypePrice?.["Non-VIP"]?.min &&
                      data?.purchaseSettings?.defaultBuyNowCandidateStatus !== "disabled" && (
                        <>
                          <div
                            // onClick={() => {
                            //   scrollToBuy();
                            //   setIsSelected(true);
                            // }}
                            onClick={handleRequestQuote}
                            className="w-full mt-4 cursor-pointer group border border-amber-200 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/10 dark:border-amber-900/30 rounded-lg p-4 flex items-center justify-center gap-3 transition-colors"
                          >
                            <span className="text-amber-700 dark:text-amber-500 font-semibold group-hover:underline">
                              Need custom requirements? Request a Quote
                            </span>
                            <svg
                              className="w-5 h-5 text-amber-600 transform group-hover:translate-x-1 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 8l4 4m0 0l-4 4m4-4H3"
                              />
                            </svg>
                          </div>
                        </>
                      )}
                  </div>
                </div>
              )}
          </div>
        </div>
        <div
          ref={productInfoMobileRef}
          className="mt-4 md:mt-10 px-2 pb-6 lg:pb-20 md:pb-0  lg:hidden"
        >
          <ProductInfo
            product={data?.product}
            selectedKey={productInfoTab}
            onSelectionChange={(key) => setProductInfoTab(String(key))}
          />
        </div>
        {/* APPA Trust Badge */}
        <Link to="/guarantees" className=" px-2 lg:hidden">
          <Image
            alt="appa-sponsor"
            className="mt-4"
            radius="md"
            src={appaImg}
            removeWrapper
            fallbackSrc
          />
        </Link>

        {/* ── Bottom: Description + Details/Reviews (full width) ── */}
      </div>

      {data && data.product && (
        <Suspense fallback={null}>
          <SimilarProduct userType={user?.userType || UserType.Non_VIP} product={data.product} />
        </Suspense>
      )}

      {/* ─── Sticky Footer (Always on Mobile, Conditional on Desktop) ─── */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] transition-transform duration-300 transform ${
          showStickyBar ? "translate-y-0" : "translate-y-full md:translate-y-full"
        } md:translate-y-0 lg:hidden block`}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Price Info (Mobile optimized) */}
          <div className="flex items-center justify-between w-full sm:w-auto gap-4">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                Total
              </span>
              <span className="text-sm font-bold text-primary dark:text-white leading-none">
                ${formatToTwoDecimalPlaces(currentPrice?.grandTotal || 0)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                Unit Price
              </span>
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                ${formatToTwoDecimalPlaces(currentPrice?.actualUnitPrice || 0)}{" "}
                <span className="text-xs font-normal text-gray-400">ea</span>
              </span>
            </div>
            <button
              type="button"
              onClick={scrollToQuantitySection}
              className="flex flex-col text-xs items-center gap-0.5 border border-gray-200 rounded-md p-0.5 transition-colors hover:bg-zinc-50 cursor-pointer"
            >
              <div>Qty</div>
              <div>{productQty.toLocaleString("en-US")}</div>
            </button>
            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              {shouldShowRequestQuote ? (
                <Button
                  variant="solid"
                  color="warning"
                  radius="full"
                  size="sm"
                  onPress={handleRequestQuote}
                >
                  Request Quote
                </Button>
              ) : (
                <>
                  <Button
                    variant="solid"
                    color="primary"
                    radius="sm"
                    size="sm"
                    startContent={<BsCart2 />}
                    isLoading={isAddingToCartLoading}
                    isDisabled={
                      productQty <= 0 ||
                      productQty < (data?.product?.quantity?.min || 1) ||
                      !availableStock ||
                      data?.purchaseSettings?.defaultBuyNowCandidateStatus === "disabled"
                    }
                    onPress={handleBuyNow}
                  >
                    {user?.isCreditAccount ? "Place Order" : "Buy Now"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sticky Bar (Original Logic) */}
      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 z-50 pl-0 lg:pl-40 bg-white dark:bg-zinc-900 border-t-5 border-primary dark:border-zinc-800 p-4 !py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] transition-transform duration-300 transform translate-y-0 hidden lg:block">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Left: Price Info */}
            <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                  Total Price
                </span>
                <span className="text-xl font-bold text-primary dark:text-white">
                  ${formatToTwoDecimalPlaces(currentPrice?.grandTotal || 0)}
                </span>
              </div>
              <div className="h-8 w-px bg-zinc-200 dark:bg-zinc-700 hidden sm:block"></div>
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                  Unit Price
                </span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  ${formatToTwoDecimalPlaces(currentPrice?.actualUnitPrice || 0)}{" "}
                  <span className="text-xs font-normal text-gray-400">ea</span>
                </span>
              </div>
              <button
                type="button"
                onClick={scrollToQuantitySection}
                className="flex flex-col items-center gap-0.5 border border-gray-200 rounded-md p-1 transition-colors hover:bg-zinc-50 cursor-pointer"
              >
                <div>Qty</div>
                <div>{productQty.toLocaleString("en-US")}</div>
              </button>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {shouldShowRequestQuote ? (
                <Button
                  variant="solid"
                  color="warning"
                  radius="sm"
                  className="flex-1 sm:flex-none font-bold text-white shadow-md"
                  startContent={<TfiWrite />}
                  onPress={handleRequestQuote}
                >
                  Request Quote
                </Button>
              ) : (
                <>
                  <Button
                    variant="solid"
                    color="primary"
                    radius="sm"
                    className="flex-1 sm:flex-none font-bold text-white shadow-md"
                    startContent={<BsCart2 />}
                    isLoading={isAddingToCartLoading}
                    isDisabled={
                      productQty <= 0 ||
                      productQty < (data?.product?.quantity?.min || 1) ||
                      !availableStock ||
                      data?.purchaseSettings?.defaultBuyNowCandidateStatus === "disabled"
                    }
                    onPress={handleBuyNow}
                  >
                    {user?.isCreditAccount ? "Place Order" : "Buy Now"}
                  </Button>
                  <Button
                    isIconOnly
                    variant="flat"
                    color="primary"
                    radius="sm"
                    aria-label="Send Pricing"
                    className="hidden sm:flex"
                    isDisabled={!availableStock}
                    onPress={onOpen}
                  >
                    <GiPriceTag className="text-lg" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <SendPriceModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        defaultValues={{
          firstName: data?.user?.firstName,
          lastName: data?.user?.lastName,
          email: data?.user?.email?.address,
        }}
        productPricingData={{
          productId: data.product?._id,
          productImageUrl: data?.product?.overview?.heroImage,
          productUrl: data.url,
          quantity: productQty,
          brandingOption: mainAdditions?.description as string,
          color: selectedColor?.name as string,
          itemPrice: `${formatToTwoDecimalPlaces(currentPrice?.actualUnitPrice) || 0}`,
          totalPrice: `${formatToTwoDecimalPlaces(currentPrice?.grandTotal || 0)}`,
          decorationType: currentBrandingType?.friendlyName,
          brandingPositions:
            selectedBrandingPosition.friendlyName || selectedBrandingPosition.additionName,
        }}
      />

      {requestIsOpen && (
        <Suspense fallback={null}>
          <RequestForm
            stockData={null}
            isOpen={requestIsOpen}
            onOpenChange={requestOnOpenChange}
            currentQuoteProduct={quoteProduct}
            user={data?.user}
            onClose={requestOnClose}
            url=""
          />
        </Suspense>
      )}
      {/* JSON-LD scripts for SEO, only render if data and data.product are defined */}
      {data && data.product && (
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(productPageSchema(data.product)),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: category,
                    item: `${data.origin}/category/${category}`,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: subcategory,
                    item: `${data.origin}/category/${category}/${subcategory}`,
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: data?.product?.overview?.name,
                    item: data.url || "",
                  },
                ],
              }),
            }}
          />
        </>
      )}
    </Fragment>
  );
});
